/* Eloss — cart mutations (quantity, remove, note)
 *
 * Spec: §H.1, §H.2, §H.3, §I.3.5 rule 5, §P.5, §P.6.
 *
 * §H.1 IS THE ARCHITECTURE: "All cart mutations use the Cart AJAX API with
 * sections=cart-drawer,cart-bubble (plus main-cart on the cart page) and swap
 * the returned HTML. No client-side cart state."
 *
 * So this file never holds a cart. It sends a mutation, receives server-rendered
 * HTML, and swaps it in. That is not a stylistic preference — it is what makes
 * §I.3.5 rule 5 structurally true: `_`-prefixed line item properties survive
 * quantity changes and removals because nothing here ever reads, parses,
 * rebuilds or re-submits a line. Shopify's response IS the cart.
 *
 * PROGRESSIVE ENHANCEMENT. Every control this file enhances already works
 * without it: quantities are `updates[]` inputs in a real form that posts to
 * /cart, and remove is a real link to /cart/change?line=N&quantity=0. With this
 * file absent the cart page is fully operable (§H.3).
 *
 * This element sits INSIDE the subtree it replaces, so — exactly as in
 * component-variant-picker.js — every DOM write happens last and nothing
 * touches `this` afterwards.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  const NOTE_DEBOUNCE = 500;

  Eloss.define(
    'eloss-cart-items',
    class ElossCartItems extends HTMLElement {
      connectedCallback() {
        this.changeUrl = this.dataset.changeUrl;
        this.updateUrl = this.dataset.updateUrl;
        this.onCartPage = this.dataset.context === 'page';

        this.onClick = (event) => this.handleClick(event);
        this.onChange = (event) => this.handleChange(event);
        this.addEventListener('click', this.onClick);
        this.addEventListener('change', this.onChange);

        const note = this.querySelector('[data-cart-note]');
        if (note) {
          this.onNote = Eloss.debounce(() => this.saveNote(note.value), NOTE_DEBOUNCE);
          note.addEventListener('input', this.onNote);
          this.note = note;
        }
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('change', this.onChange);
        if (this.note && this.onNote) this.note.removeEventListener('input', this.onNote);
        if (this.controller) this.controller.abort();
      }

      /** Sections to request. main-cart is added only where it exists (§H.1). */
      sectionList() {
        return this.onCartPage
          ? 'cart-drawer,cart-bubble,main-cart'
          : 'cart-drawer,cart-bubble';
      }

      handleClick(event) {
        const remove = event.target.closest('[data-cart-remove]');
        if (!remove) return;
        /* The link is a working no-JS fallback; intercept only when we can
           actually do better. */
        event.preventDefault();
        const line = Number(remove.dataset.cartRemove);
        const title = remove.closest('.cart-line')?.querySelector('.cart-line__title')?.textContent.trim();
        this.mutate(this.changeUrl, { line, quantity: 0 }, { removedTitle: title });
      }

      handleChange(event) {
        const input = event.target.closest('input[type="number"][data-line]');
        if (!input) return;
        const line = Number(input.dataset.line);
        const quantity = Math.max(0, Number(input.value) || 0);
        this.mutate(this.changeUrl, { line, quantity });
      }

      saveNote(value) {
        this.mutate(this.updateUrl, { note: value }, { silent: true });
      }

      setBusy(isBusy) {
        this.toggleAttribute('aria-busy', isBusy);
        this.classList.toggle('is-busy', isBusy);
      }

      async mutate(url, payload, options = {}) {
        if (!url) return;
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        this.setBusy(true);

        const body = { ...payload, sections: this.sectionList(), sections_url: window.location.pathname };

        try {
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
            signal: this.controller.signal,
          });

          const data = await response.json();

          if (!response.ok) {
            Eloss.emit(Eloss.EVENTS.cartError, { source: 'cart-items', data });
            this.announce(data.description || data.message || '');
            this.setBusy(false);
            return;
          }

          /* DOM writes last — the swap destroys this element. */
          window.Eloss.cartSwap(data.sections, {
            announce: options.silent
              ? null
              : options.removedTitle
                ? { key: 'removed', title: options.removedTitle }
                : { key: 'updated' },
          });

          Eloss.emit(Eloss.EVENTS.cartUpdated, { itemCount: data.item_count });
        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('Eloss: cart mutation failed', error);
          Eloss.emit(Eloss.EVENTS.cartError, { source: 'cart-items', error: String(error) });
          this.setBusy(false);
        }
      }

      announce(message) {
        const live = document.querySelector('[data-cart-live]');
        if (live && message) live.textContent = message;
      }
    }
  );

  /* ======================================================================
     The shared swap.
     Exposed on window.Eloss so the product form, the cart items and quick-add
     all replace the same targets in the same way. One implementation, so the
     drawer and the header count can never disagree about what the cart is.
     ====================================================================== */

  function swapSections(sections, options = {}) {
    if (!sections) return;
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    /* The drawer: replace the dialog's contents, never the custom element that
       owns open/closed state. */
    if (sections['cart-drawer']) {
      const doc = parse(sections['cart-drawer']);
      const nextDialog = doc.querySelector('[data-cart-dialog]');
      const currentDialog = document.querySelector('[data-cart-dialog]');
      if (nextDialog && currentDialog) {
        currentDialog.innerHTML = nextDialog.innerHTML;
      }
      const host = document.querySelector('[data-cart-drawer]');
      const nextHost = doc.querySelector('[data-cart-drawer]');
      if (host && nextHost) host.dataset.itemCount = nextHost.dataset.itemCount;
    }

    /* The header badge. cart-bubble is the authoritative count (decision 14.1);
       the phase 2 header markup around it is left exactly as it was. */
    if (sections['cart-bubble']) {
      const doc = parse(sections['cart-bubble']);
      const count = doc.querySelector('[data-cart-count]')?.textContent.trim();
      const text = doc.querySelector('[data-cart-count-text]')?.textContent.trim();
      const badge = document.querySelector('[data-header-cart-count]');
      const badgeText = document.querySelector('[data-header-cart-count-text]');
      const isEmpty = count === '0';
      if (badge && count !== undefined) {
        badge.textContent = count;
        badge.hidden = isEmpty;
      }
      if (badgeText && text) {
        badgeText.textContent = text;
        badgeText.hidden = isEmpty;
      }
    }

    if (sections['main-cart']) {
      const doc = parse(sections['main-cart']);
      const next = doc.querySelector('[data-cart-page]');
      const current = document.querySelector('[data-cart-page]');
      if (next && current) current.innerHTML = next.innerHTML;
    }

    if (options.announce) {
      const live = document.querySelector('[data-cart-live]');
      const template = live?.dataset[options.announce.key];
      if (live && template) {
        live.textContent = template.replace('__TITLE__', options.announce.title || '');
      }
    }
  }

  window.Eloss.cartSwap = swapSections;
})();
